/**
 * Sección "Mira cómo se ve por dentro": título + dos collages de páginas superpuestas, cada uno con su
 * subtítulo. Las imágenes están alojadas en Magic Patterns y se pegaron con fondo blanco. Al cargar se
 * aplanan sobre el crema en un canvas (multiplicar: el blanco pasa a crema y las sombras se conservan),
 * así quedan como una imagen opaca con su propio fondo, igual que las de los bonos: se ve igual en modo
 * claro y en modo oscuro, sin depender de ningún blend del navegador.
 */
const COLLAGE_1 = "https://cdn.magicpatterns.com/uploads/r3vjTjNqywDwUnNQjzps2T/image.png";
const COLLAGE_2 = "https://cdn.magicpatterns.com/uploads/iedf9Pc8SPECydpdaM6zDq/image.png";
const CREAM = "#efdbb3";

export const INSIDE_HTML = `<!-- POR DENTRO -->
<style>
.peek2 .peek2-grid{display:grid;gap:clamp(28px,4vw,48px);margin:34px auto 0;max-width:1100px}
.peek2 figure{margin:0}
.peek2 figure img,.peek2 figure canvas{width:100%;height:auto;display:block;border-radius:20px;background:${CREAM}}
.peek2 figcaption{text-align:center;max-width:52ch;margin:0 auto 12px}
.peek2 figcaption h3{font-size:clamp(1.2rem,2.2vw,1.5rem)}
.peek2 figcaption p{margin:6px 0 0;color:var(--mute);font-size:.98rem;line-height:1.5}
@media(max-width:560px){
  .peek2 .peek2-grid{margin-top:24px;gap:26px}
  .peek2 figure img,.peek2 figure canvas{margin-inline:-10px;width:calc(100% + 20px);max-width:none;border-radius:14px}
  .peek2 figcaption p{font-size:.9rem}
}
</style>
<section class="section peek2" id="por-dentro">
  <div class="wrap">
    <div class="center rv">
      <p class="eyebrow">Por dentro</p>
      <h2 style="margin-top:12px">Mira cómo se ve <em style="font-style:italic;font-weight:600;color:var(--clay)">por dentro</em></h2>
      <p class="lead" style="margin-top:14px">Material pensado para usarse, no para quedar guardado. Anota tu avance sin complicarte.</p>
    </div>
    <div class="peek2-grid rv">
      <figure>
        <figcaption>
          <h3>Capítulos ilustrados</h3>
          <p>Cómo aprende tu perro, premios que funcionan, el momento justo y tu casa lista para entrenar.</p>
        </figcaption>
        <img data-cut src="${COLLAGE_1}" alt="Aperturas de capítulo: 01 Cómo aprende, 02 Premios que funcionan, 03 El momento justo, 04 Tu casa lista para entrenar" loading="lazy" decoding="async" crossorigin="anonymous">
      </figure>
      <figure>
        <figcaption>
          <h3>Guías visuales paso a paso</h3>
          <p>Los cuatro tipos de premio, tu zona de entrenamiento y tu primera sesión de 10 minutos.</p>
        </figcaption>
        <img data-cut src="${COLLAGE_2}" alt="Páginas interiores: tu perro repite lo que le funciona, los cuatro tipos de premio, tu zona de entrenamiento y tu primera sesión de 10 minutos" loading="lazy" decoding="async" crossorigin="anonymous">
      </figure>
    </div>
  </div>
</section>

`;

/** Aplana la imagen sobre el color crema (multiplicar) y devuelve un canvas opaco, o null si falla. */
function flatten(img: HTMLImageElement): HTMLCanvasElement | null {
  try {
    const w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) return null;
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    const x = c.getContext("2d");
    if (!x) return null;
    x.fillStyle = CREAM;
    x.fillRect(0, 0, w, h);
    x.globalCompositeOperation = "multiply";
    x.drawImage(img, 0, 0);
    x.getImageData(0, 0, 1, 1); // falla si la imagen no es legible (sin CORS)
    return c;
  } catch {
    return null; // queda la imagen original
  }
}

export function initInside(root: HTMLElement): () => void {
  let dead = false;
  root.querySelectorAll<HTMLImageElement>("img[data-cut]").forEach((img) => {
    const run = () => {
      if (dead || !img.isConnected) return;
      const c = flatten(img);
      if (!c) return;
      c.setAttribute("role", "img");
      c.setAttribute("aria-label", img.alt);
      img.replaceWith(c);
    };
    if (img.complete && img.naturalWidth) run();
    else img.addEventListener("load", run, { once: true });
  });
  return () => { dead = true; };
}
